// Static config: navigation, hero slides, mega menu taxonomy

// Hero A-spot slides.
// All 7 artworks are shipped at 2172x724 (3:1 aspect) under /public/banner/.
// The full CTA/copy is baked into the illustration itself, so only the image
// and the destination href matter at runtime — `eyebrow` / `title` / etc. are
// preserved only for screen-reader accessibility. Keep the filenames in
// `image` matching /public/banner exactly (spaces included) since we don't
// URL-encode client-side; the browser handles that for image src.
export const HERO_SLIDES = [
  {
    id: 1,
    eyebrow: "AI Dental Assistant",
    title: "Dr. Dyntra — AI Dental Consultant",
    subtitle: "AI-Powered Diagnostics & Insights",
    description: "Your intelligent partner for diagnostics, treatment planning, and clinic growth.",
    cta_primary: { label: "Explore Dr. Dyntra", href: "/ai-consultant" },
    cta_secondary: { label: "Watch Demo", href: "/ai-consultant" },
    bg: "linear-gradient(135deg, #361D82 0%, #4B2AAD 55%, #6B4CD9 100%)",
    image: "/banner/Dr Dyntra.png",
  },
  {
    id: 2,
    eyebrow: "Complete Dental Ecosystem",
    title: "Everything for the Modern Dental Practice",
    subtitle: "Products. Services. Learning. All under one roof.",
    description: "Discover the full Dyntra catalog — consumables, equipment, services and more.",
    cta_primary: { label: "Shop Now", href: "/shop" },
    cta_secondary: { label: "Explore", href: "/shop" },
    bg: "linear-gradient(135deg, #361D82 0%, #6B4CD9 100%)",
    image: "/banner/Complete Dental Ecosystem.png",
  },
  {
    id: 3,
    eyebrow: "Offers",
    title: "Buy More, Save More",
    subtitle: "Bulk pricing on your favourite dental supplies",
    description: "Unlock deeper discounts as quantity increases across 12,000+ SKUs.",
    cta_primary: { label: "Shop Offers", href: "/offers" },
    cta_secondary: { label: "See Deals", href: "/offers" },
    bg: "linear-gradient(135deg, #4B2AAD 0%, #6B4CD9 100%)",
    image: "/banner/Buy More Save More.png",
  },
  {
    id: 4,
    eyebrow: "Hire Hub",
    title: "Hire Dental Professionals",
    subtitle: "Verified dentists, at your fingertips",
    description: "Post a role or apply — India's largest dental talent marketplace.",
    cta_primary: { label: "Explore Hire Hub", href: "/hire-hub" },
    cta_secondary: { label: "Learn More", href: "/hire-hub" },
    bg: "linear-gradient(135deg, #361D82 0%, #4B2AAD 100%)",
    image: "/banner/Hire Hub.png",
  },
  {
    id: 5,
    eyebrow: "Dyntra Clinics",
    title: "Own & Grow a Clinic",
    subtitle: "Franchise the Dyntra way",
    description: "Turnkey clinic setup, trained staff, and Dyntra's brand power behind you.",
    cta_primary: { label: "Explore Clinics", href: "/clinic-setup" },
    cta_secondary: { label: "Become a Partner", href: "/partner" },
    bg: "linear-gradient(135deg, #6B4CD9 0%, #361D82 100%)",
    image: "/banner/clinics.png",
  },
  {
    id: 6,
    eyebrow: "Events",
    title: "Dental Events & CDE",
    subtitle: "Workshops. Livestreams. Conferences.",
    description: "Learn from experts. Earn certificates. Grow your practice.",
    cta_primary: { label: "View Events", href: "/events" },
    cta_secondary: { label: "Notify Me", href: "/events" },
    bg: "linear-gradient(135deg, #4B2AAD 0%, #FFB020 130%)",
    image: "/banner/dental events.png",
  },
  {
    id: 7,
    eyebrow: "Coming Soon",
    title: "Upcoming Launch",
    subtitle: "Big things brewing at Dyntra",
    description: "Get notified when our next product line goes live.",
    cta_primary: { label: "Notify Me", href: "/notifications" },
    cta_secondary: { label: "Learn More", href: "/notifications" },
    bg: "linear-gradient(135deg, #361D82 0%, #4B2AAD 100%)",
    image: "/banner/upcoming launch.png",
  },
];

// (HERO_TRUST_STRIP was removed — the homepage now uses the standalone
//  `USPStrip` component right below the hero banner instead.)

// What Brings You Here cards.
// New illustrations shipped at 1016x1548 (0.656:1 = 191:291), so the WBH
// tile aspect in WhatBringsYouHere.jsx should match `aspect-[1016/1548]` for
// pixel-perfect fit with no crop / no letterbox. Every card's `image` path
// mirrors the exact filename in /public/wbh (spaces / underscores included).
export const WBYH_CARDS = [
  {
    id: "hire-hub",
    title: "Hire Hub",
    subtitle: "Hire or get hired",
    image: "/wbh/Hire_Hub.png",
    actions: [
      { label: "Hire Me", href: "/hire-hub?open=hireme" },
      { label: "Hire For Me", href: "/hire-hub?open=hireforme" },
    ],
  },
  {
    id: "dyntra-connect",
    title: "Dyntra Connect",
    subtitle: "Manage My Clinic",
    badge: "Coming Soon",
    image: "/wbh/Dyntra Connect.png",
    actions: [{ label: "Learn More", href: "/clinic-setup" }],
  },
  {
    id: "find-consultant",
    title: "Find Me Consultant",
    subtitle: "Expert guidance",
    image: "/wbh/Find me consultant.png",
    actions: [
      { label: "I Am", href: "/consultation?mode=consultant" },
      { label: "I Need", href: "/consultation?mode=need" },
    ],
  },
  {
    id: "dyntra-clinics",
    title: "Dyntra Clinics",
    subtitle: "Own & grow clinics",
    badge: "Franchisee Soon",
    image: "/wbh/Dyntra Clinic.png",
    actions: [{ label: "Explore", href: "/clinic-setup" }],
  },
  {
    id: "dr-dyntra",
    title: "Dr. Dyntra",
    subtitle: "AI-Powered Assistant",
    image: "/wbh/Dr Dyntra.png",
    actions: [{ label: "Explore Now", href: "/ai-consultant" }],
  },
  {
    id: "refurbished",
    title: "Refurbished",
    subtitle: "Pre-owned equipment",
    image: "/wbh/Refurbished.png",
    actions: [{ label: "Browse Products", href: "/refurbished" }],
  },
  {
    id: "events",
    title: "Events",
    subtitle: "Workshops & webinars",
    image: "/wbh/events.png",
    actions: [{ label: "View Events", href: "/events" }],
  },
];

// Mega menu categories (L1 -> L2 -> L3 subset)
export const MEGA_CATEGORIES = [
  { slug: "endodontics", name: "Endodontics", groups: [
    { title: "Access Cavity Preparation", items: ["Endodontic Burs", "Carbide Burs", "Endo Access Burs", "HP Series Burs", "RA Series Burs", "Diamond Burs"] },
    { title: "Shape", items: ["Endodontic Files", "NiTi Rotary Files", "NiTi Hand Files", "K Files", "H Files"] },
    { title: "Obturate", items: ["Gutta Percha Points", "Paper Points", "Root Canal Sealants", "Spreaders & Pluggers"] },
    { title: "Post & Core", items: ["Metal Posts", "Fibre Posts", "Core Build-up Materials", "Peeso Reamers"] },
  ]},
  { slug: "orthodontics", name: "Orthodontics", groups: [
    { title: "Conventional Braces", items: ["Metal", "Mini Twin", "Low Profile", "Cosmetic", "Ceramic"] },
    { title: "New Age Braces", items: ["Self-ligating", "Metal (SL)", "Ceramic (SL)", "Aligners", "Lingual"] },
    { title: "Anchorage", items: ["Bands & Attachments", "Buccal Tubes", "TADs", "Orthopedic Appliances"] },
    { title: "Archwires & Elastics", items: ["NiTi", "Stainless Steel", "Ligature Ties", "E Chains"] },
  ]},
  { slug: "prosthodontics", name: "Prosthodontics", groups: [
    { title: "Restoratives", items: ["Composites", "Packable", "Flowable", "Glass Ionomers", "Amalgam"] },
    { title: "Bonding Agents", items: ["Self Etch Adhesives", "Total Etch", "Etchants", "Resin Cements"] },
    { title: "Crown & Bridge", items: ["Tooth Preparation Burs", "Temporisation", "Primers"] },
    { title: "Esthetic & Cosmetic", items: ["Bleaching", "In-Office", "At-Home", "Tooth Jewellery"] },
  ]},
  { slug: "implantology", name: "Implantology", groups: [
    { title: "Implants", items: ["Dental Implants", "Implant Drills", "Implant Accessories", "Implant Abutments"] },
  ]},
  { slug: "oral-surgery", name: "Oral Surgery", groups: [
    { title: "Oral & Maxillofacial", items: ["Local Anaesthetics", "Post Extraction Dressings", "Surgical Accessories"] },
  ]},
  { slug: "periodontics", name: "Periodontics", groups: [
    { title: "Periodontics", items: ["Surgical Products", "Astringents/Hemostatics", "Bone Grafts", "Splints"] },
  ]},
  { slug: "paedodontics", name: "Paedodontics", groups: [
    { title: "Paedodontics", items: ["Pediatric Crowns", "Pulp Therapy", "Pedodontic Files"] },
  ]},
  { slug: "dental-equipment", name: "Equipment", groups: [
    { title: "Dental Chairs", items: ["Dental Chairs", "Dental Stools", "Compressors", "Suction Units"] },
    { title: "General", items: ["Ultrasonic Scalers", "Amalgamators", "Air Polishing Units"] },
    { title: "Sterilization", items: ["Dental Autoclaves", "Ultrasonic Cleaners", "UV Cabinets"] },
    { title: "Imaging", items: ["RVG", "X-Ray Machines", "Intraoral Scanners"] },
  ]},
  { slug: "hand-pieces", name: "Handpieces", groups: [
    { title: "Handpieces", items: ["High Speed", "Low Speed", "Airrotor Cartridges", "Micromotors", "Endo Handpieces"] },
  ]},
  { slug: "instruments", name: "Instruments", groups: [
    { title: "General", items: ["Mirrors", "Probes", "Tweezers", "Tray Kits"] },
    { title: "Endo", items: ["Hand Cutting", "Filling", "Composite Instruments", "RCT Instruments"] },
    { title: "Perio", items: ["Scalers", "Curettes", "Periodontal Probes", "Elevators"] },
    { title: "Surgical", items: ["Scalpels", "Needle Holders", "Forceps", "Bone Cutting"] },
  ]},
  { slug: "dentaltech", name: "DentalTech", groups: [
    { title: "Imaging Systems", items: ["RVG", "X-Ray Machines", "Intraoral Scanners"] },
    { title: "Small Equipment", items: ["Light Cure Units", "Intra Oral Cameras", "Loupes"] },
  ]},
  { slug: "laboratory", name: "Laboratory", groups: [
    { title: "Prostho Lab", items: ["Model Creation", "Acrylics", "Dental Waxes", "Teeth Sets"] },
  ]},
  { slug: "impression-material", name: "Impressions", groups: [
    { title: "Impression Materials", items: ["Putty", "Alginates", "Bite Registration", "Impression Trays"] },
  ]},
  { slug: "basic-dental-products", name: "Basic Supplies", groups: [
    { title: "Gloves", items: ["Latex Powdered", "Latex Powder-free", "Nitrile", "Surgical"] },
    { title: "Infection Control", items: ["Sterilization Rolls", "Instrument Disinfectants", "Hand Sanitizers"] },
    { title: "Protective Gear", items: ["Patient Bibs", "Doctor Coats", "Face Shields"] },
    { title: "Masks & Caps", items: ["3-Ply Masks", "Special Masks", "Head Caps"] },
  ]},
  { slug: "pharmacy", name: "Pharmacy", groups: [
    { title: "Preventive", items: ["Fluoride Gels", "Fluoride Varnishes", "Pit & Fissure Sealants"] },
    { title: "Oral Care", items: ["Toothbrushes", "Interdental", "Flossing"] },
  ]},
];

// Primary nav (below the hamburger)
export const NAV_LINKS = [
  { label: "Shop", href: "/shop", type: "link" },
  { label: "Services", href: "#", type: "dropdown", items: [
    { label: "Find Me Consultant", desc: "Expert guidance for your clinic", href: "/consultation" },
    { label: "Hire Hub", desc: "Hire or get hired in dental", href: "/hire-hub" },
    { label: "Dr. Dyntra", desc: "AI-Powered Dental Assistant", href: "/ai-consultant" },
    { label: "Dyntra Connect", desc: "Manage My Clinic (Coming Soon)", href: "/clinic-setup" },
  ]},
  { label: "Events", href: "/events", type: "link" },
  { label: "Refurbished", href: "/refurbished", type: "link" },
  { label: "Offers of the Day", href: "/offers", type: "link", highlight: true },
  { label: "Rewards", href: "/rewards", type: "link" },
  { label: "Support", href: "/support", type: "link" },
];

export const USP_ITEMS = [
  { icon: "ShieldCheck", label: "100% Genuine", sub: "Products" },
  { icon: "Users", label: "Trusted by", sub: "Dental Professionals" },
  { icon: "Lock", label: "Secure", sub: "Payments" },
  { icon: "Truck", label: "Fast", sub: "Delivery" },
  { icon: "RotateCcw", label: "Easy", sub: "Returns" },
  { icon: "Gift", label: "Rewards", sub: "on Every Purchase" },
];
